import React, { FC, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid
} from "@mui/material";
import { useForm, useWatch } from "react-hook-form";
import { InfoFilledIcon } from "assets";
import { UnitDescription, createCollector, getSiteById, updateCollector } from "api";
import { useGetAllowedValues, useNotify, useAmplitude } from "hooks";
import { QUERY_KEYS } from "consts";
import { checkNullValue, formatDateToISOStartOfDay } from "utils";
import { CollectorModalProps, FormData } from "./types";
import { fieldStyles } from "./styles";
import {
    createInitialFormValues,
    getModalTitle,
    checkSubtypeNeeded,
    areMandatoryFieldsFilled,
    processUnitCostForSubmission
} from "./helpers";
import { FormFields, ActionButtons, StepHeader } from "./components";

export const CollectorModal: FC<CollectorModalProps> = ({
    isOpen,
    onClose,
    site,
    editCollector,
    isNotesOnlyMode,
    onCollectorDataUpdate,
    clickType,
    setNewSite,
    currentSiteSelected,
    allowedValues: externalAllowedValues,
    currentBillData,
    setNewCollectorId,
    stepHeader,
    dialogPaperSx,
}) => {
    const notify = useNotify();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { trackEvent } = useAmplitude();
    const { data: utilityMapping } = useGetAllowedValues();

    // Use external allowedValues if provided, otherwise use the hook
    const allowedValues = externalAllowedValues || utilityMapping;

    const utilityTypesObjectRequiringUtilitySubType = allowedValues?.utilitySubtypesMap || {};
    const utilityTypesNamesRequiringUtilitySubType = Object.keys(utilityTypesObjectRequiringUtilitySubType) || [];

    const { mutateAsync: createCollectorRequest } = useMutation(createCollector);
    const { mutateAsync: updateCollectorRequest } = useMutation(updateCollector);

    const endDate = clickType === "Disable"
        ? formatDateToISOStartOfDay(new Date())
        : clickType === "Enable"
            ? "2999-12-31T00:00:00Z"
            : editCollector?.endDate || null;

    const initialFormValues = createInitialFormValues(
        editCollector,
        currentBillData,
        endDate
    );

    const { control, handleSubmit, reset, setValue } = useForm<FormData>({
        mode: "onChange",
        defaultValues: initialFormValues,
    });

    const [isSpendBasedEstimation, setIsSpendBasedEstimation] = useState<boolean>(
        editCollector?.spendBased || currentBillData?.spendBased || false
    );
    const [availableUnitDescriptions, setAvailableUnitDescriptions] = useState<UnitDescription[]>([]);
    const [notesError, setNotesError] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [showWarning, setShowWarning] = useState<boolean>(false);

    const watchedUtilityType = useWatch({ control, name: "utilityTypeName" });
    const watchedConsumptionUnit = useWatch({ control, name: "consumptionUnit" });

    // Watch all form values for validation
    const formValues = useWatch({ control });

    // Check if all mandatory fields are filled
    const isFormValid = areMandatoryFieldsFilled(
        {
            utilityTypeName: formValues.utilityTypeName || "",
            utilitySubType: formValues.utilitySubType || "",
            accountNumber: formValues.accountNumber || "",
            startDate: formValues.startDate || "",
            endDate: formValues.endDate || "",
            frequency: formValues.frequency || "",
            consumptionUnit: formValues.consumptionUnit || "",
            currency: formValues.currency || "",
            scope: formValues.scope || "",
            allowedDocuments: formValues.allowedDocuments || "",
        },
        utilityTypesNamesRequiringUtilitySubType,
        utilityTypesObjectRequiringUtilitySubType
    );

    // Reset form when modal opens with new data
    useEffect(() => {
        if (isOpen) {
            const newInitialValues = createInitialFormValues(
                editCollector,
                currentBillData,
                endDate
            );
            reset(newInitialValues);
            setIsSpendBasedEstimation(editCollector?.spendBased || currentBillData?.spendBased || false);
        }
    }, [isOpen, editCollector, currentBillData, reset, endDate]);

    // Update available unit descriptions when utility type changes
    useEffect(() => {
        if (watchedUtilityType) {
            const selectedUtilityTypeInfo = allowedValues?.utilityTypes?.find(
                (ut: any) => ut.name.toLowerCase() === watchedUtilityType.toLowerCase()
            );
            if (selectedUtilityTypeInfo) {
                setAvailableUnitDescriptions(selectedUtilityTypeInfo.unitDescription || []);
            }
        }

        if (editCollector && (watchedUtilityType !== initialFormValues.utilityTypeName || watchedConsumptionUnit !== initialFormValues.consumptionUnit)) {
            setShowWarning(true);
        } else {
            setShowWarning(false);
        }
    }, [watchedUtilityType, watchedConsumptionUnit, allowedValues, initialFormValues.utilityTypeName, initialFormValues.consumptionUnit, editCollector]);

    const handleClose = () => {
        reset(initialFormValues);
        setIsSpendBasedEstimation(false);
        setNotesError(false);
        onClose();
    };

    const onSubmit = async (data: FormData) => {
        const isEdit = !!editCollector;

        // Validation
        if (checkSubtypeNeeded(data.utilityTypeName, utilityTypesNamesRequiringUtilitySubType) &&
            !data.utilitySubType &&
            !utilityTypesObjectRequiringUtilitySubType[data.utilityTypeName]?.optional) {
            notify.error("Utility Subtype is required.");
            return;
        }

        setIsSubmitting(true);

        const baseRequestData = {
            accountNumber: checkNullValue(data.accountNumber),
            allowedDocuments: Array.isArray(data.allowedDocuments)
                ? checkNullValue(data.allowedDocuments)
                : [data.allowedDocuments],
            consumptionUnit: checkNullValue(data.consumptionUnit),
            currency: checkNullValue(data.currency),
            startDate: checkNullValue(data.startDate),
            endDate: checkNullValue(data.endDate),
            frequency: checkNullValue(data.frequency),
            meterNumber: checkNullValue(data.meterNumber),
            providerName: checkNullValue(data.providerName),
            scope: checkNullValue(data.scope),
            isSpendBased: isSpendBasedEstimation,
            utilitySubType: checkSubtypeNeeded(data.utilityTypeName, utilityTypesNamesRequiringUtilitySubType)
                ? checkNullValue(data.utilitySubType)
                : null,
            siteId: site?.id || currentSiteSelected?.siteId || 0,
            utilityTypeId: allowedValues?.utilityTypes?.find(
                (ut: any) => ut.name.toLowerCase() === data.utilityTypeName.toLowerCase()
            )?.id || 0,
            notes: data.notes,
            ...(isSpendBasedEstimation ? { unitCost: processUnitCostForSubmission(data.unitCost) } : {}),
        };

        try {
            if (isEdit) {
                await updateCollectorRequest({ ...baseRequestData, id: editCollector.id! });
                notify.success(isNotesOnlyMode ? "Notes successfully updated!" : "Collector successfully edited!");
            } else {
                const isDocumentSummaryPage = location.pathname.split("/")[1] === "documents";

                if (site?.enabled) {
                    trackEvent("Added Utility Provider", isDocumentSummaryPage ? "Document view - Invoice Data" : "Site Management - Active");
                };

                await createCollectorRequest(baseRequestData, {
                    onSuccess: (response) => {
                        if (setNewCollectorId && response?.data?.id) {
                            setNewCollectorId(response.data.id);
                        }
                        notify.success(`Collector successfully added.`);
                    },
                });
            }

            if (site && onCollectorDataUpdate) {
                try {
                    const freshSiteData = await getSiteById(site.id);
                    queryClient.setQueryData([QUERY_KEYS.SITES, site.id], freshSiteData);
                    queryClient.invalidateQueries([QUERY_KEYS.COLLECTORS, 'deactivated', QUERY_KEYS.SITES, site.id]);
                    onCollectorDataUpdate(freshSiteData);
                } catch (error) {
                    notify.error("Error fetching collector data");
                    console.error("Error fetching collector data:", error);
                }
            }

            // Set new site if callback provided
            if (setNewSite) {
                setNewSite({
                    id: currentSiteSelected?.siteId || 0,
                    name: currentSiteSelected?.siteName || ""
                });
            }

            handleClose();
        } catch (error: any) {
            const errorMsg = typeof error?.response?.data === 'string'
                ? error?.response?.data
                : "Some error has happened while processing the request!";
            notify.error(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isEditMode = !!editCollector;
    const modalTitle = getModalTitle(!!isNotesOnlyMode, isEditMode);

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth={false}
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: '600px',
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'hidden',
                    ...(dialogPaperSx as any || {})
                }
            }}
        >
            {
                stepHeader &&
                <StepHeader
                    currentStep={stepHeader.currentStep}
                    totalSteps={stepHeader.totalSteps}
                    onClose={handleClose}
                />
            }
            <DialogTitle sx={fieldStyles.dialogTitle} >
                {modalTitle}
            </DialogTitle >

            <DialogContent sx={fieldStyles.dialogContent}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={3}>
                        <FormFields
                            control={control}
                            setValue={setValue}
                            allowedValues={allowedValues}
                            clickType={clickType}
                            notesError={notesError}
                            setNotesError={setNotesError}
                            isNotesOnlyMode={!!isNotesOnlyMode}
                            isSpendBasedEstimation={isSpendBasedEstimation}
                            editCollector={editCollector}
                            availableUnitDescriptions={availableUnitDescriptions}
                        />
                    </Grid>
                </form>
                {showWarning && (
                    <Box
                        sx={{
                            mt: 2,
                            display: 'flex',
                            justifyContent: 'center',
                            padding: 2,
                            gap: 1,
                            backgroundColor: "#FFFBEB",
                            borderRadius: '5px',
                        }}
                    >
                        <InfoFilledIcon height="20px" width="32px" className="mt-1" />
                        <span style={{ fontSize: '12px', color: "#78350F", fontWeight: '500' }}><strong>Note: </strong>Changing Utility Type or Consumption Unit will not update any previously processed bills associated with this account number. This change will only apply to bills going forward.</span>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <ActionButtons
                    editCollector={editCollector}
                    isNotesOnlyMode={!!isNotesOnlyMode}
                    isSpendBasedEstimation={isSpendBasedEstimation}
                    isSubmitting={isSubmitting}
                    isFormValid={isFormValid}
                    onSpendBasedToggle={() => setIsSpendBasedEstimation(!isSpendBasedEstimation)}
                    onCancel={handleClose}
                    onSave={handleSubmit(onSubmit)}
                />
            </DialogActions>
        </Dialog >
    );
};
